import { Box, Button, ClickAwayListener, LinearProgress, List, Typography } from "@material-ui/core";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import Domain from "@material-ui/icons/Domain";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Virtuoso } from "react-virtuoso";


import {
    useGetVmsSearchRightsQuery,
    VmsSearchConfig,
} from "@/api/elasticSearch/vmsSearchApi";
import { endpointsLiveUnitDefaultCamera } from "@/api/liveUnits/defaultCamera";
import { useSearch } from "@/common/hooks/useSearch";
import { RootState, useDispatch, useSelector } from "@/store";
import { setMapSearchArg, setSearch } from "@/store/elasticSearch/slice";
import { setSelectedUnit } from "@/store/map/slice";
import * as FullStory from "@/utilities/hooks/fullstory";
import useFocus from "@/utilities/hooks/useFocus";
import { isUnitActive } from "@/utilities/liveUnitHelper";
import { storeRecentSearch } from "@/utilities/localStorage/recentSearches";
import { PageType } from "@/utilities/types/common";
import {
    LiveUnitSearchData,
    ResultTypes,
    Search,
    SearchIndex,
    SearchResult,
    SearchType,
    SelectedClient,
    SelectedRegion,
} from "@/utilities/types/search";

import RecentSearches from "./RecentSearches/RecentSearches";
import SearchInput from "./SearchInput/SearchInput";
import { VmsSearchResults } from "./VmsSearchResults";
import { vmsSearchStyles } from "./VmsSearchStyles";


const formSearchQueryArg = ({
    searchQuery,
    searchType,
    selectedClient,
    selectedRegion,
}: Search,
    limit: number,
): VmsSearchConfig => {
    let indices: SearchIndex[] = [];
    let orderBy = [];

    switch (searchType) {
        case SearchType.Default:
            indices = [SearchIndex.LiveUnits, SearchIndex.ArchiveCabinets];
            orderBy = [{ "location_name.keyword": { order: "asc" } }];
            break;

        case SearchType.Clients:
            indices = [SearchIndex.Clients];
            orderBy = [{ "name.keyword": { order: "asc" } }];
            break;

        case SearchType.Regions:
            indices = [SearchIndex.Regions];
            orderBy = [{ "client_name.keyword": { order: "asc" } }, { "name.keyword": { order: "asc" } }];
            break;

        default:
            indices = [SearchIndex.LiveUnits, SearchIndex.ArchiveCabinets];
            orderBy = [{ "location_name.keyword": { order: "asc" } }];
            break;
    }

    const client_id = selectedClient ? [selectedClient.id] : undefined;
    const region_id = selectedRegion ? [selectedRegion.id] : undefined;

    const sort: VmsSearchConfig["sort"] = searchQuery
        ? undefined
        : [
            { "_score": { order: "desc" } },
            ...orderBy,
        ];

    const query = searchQuery ? searchQuery : "*";

    return {
        query,
        offset: 0,
        limit,
        filter: { indices, client_id, region_id },
        sort,
    };
};

export interface Props {
    customStyle: ClassNameMap;
    pageType: PageType;
}

const SearchBar: React.FC<Props> = ({ customStyle, pageType }) => {
    const [searchOpen, setSearchOpen] = useState(false);
    const search: Search = useSelector((state: RootState) => state.elasticSearch.search);
    const { data: searchRightsData } = useGetVmsSearchRightsQuery({ indices: [SearchIndex.LiveUnits, SearchIndex.ArchiveCabinets, SearchIndex.Clients] });
    const [getStreamCameraForLiveUnit] = endpointsLiveUnitDefaultCamera.getLiveUnitDefaultStreamCameraId.useLazyQuerySubscription();
    const [inputRef, setInputFocus] = useFocus<HTMLInputElement>();

    const [searchApi, loadMore, searchQuery] = useSearch(25);
    const { isSuccess, isFetching, results, total } = searchQuery;

    const providedClasses = customStyle;
    const classes = vmsSearchStyles({ searchOpen });
    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        searchApi(formSearchQueryArg(search, 10000));
    }, [search]);

    useEffect(() => {
        if (!searchOpen && search.searchType === SearchType.Default) {
            dispatch(setMapSearchArg(formSearchQueryArg(search, 10000)));
        }
    }, [searchOpen]);


    const searchForClients = () => {
        dispatch(setSearch({ ...search, searchType: SearchType.Clients, selectedClient: null, selectedRegion: null }));
        setInputFocus();
    };

    const searchForRegions = () => {
        dispatch(setSearch({ ...search, searchType: SearchType.Regions, selectedClient: null, selectedRegion: null }));
        setInputFocus();
    };


    const openSearchBody = () => {
        setSearchOpen(true);
    };

    const closeSearchBody = () => {
        setSearchOpen(false);
    };

    const onSearchChange = (newSearch: Search, updateMap = false) => {
        dispatch(setSearch(newSearch));

        if (updateMap) {
            // This should normally be false because the map is automatically updated when the search bar loses focus
            dispatch(setMapSearchArg(formSearchQueryArg(newSearch, 10000)));
        }
    };

    const onClientSelected = (client: SelectedClient) => {
        const newSearch: Search = {
            searchQuery: "",
            searchType: SearchType.Default,
            selectedClient: client,
            selectedRegion: null,
        };

        dispatch(setSearch(newSearch));
        // Update the map
        dispatch(setMapSearchArg(formSearchQueryArg(newSearch, 10000)));
        setInputFocus();
    };
    const onRegionSelected = (region: SelectedRegion) => {
        const newSearch: Search = {
            searchQuery: "",
            searchType: SearchType.Default,
            selectedClient: null,
            selectedRegion: region,
        };

        dispatch(setSearch(newSearch));
        // Update the map
        dispatch(setMapSearchArg(formSearchQueryArg(newSearch, 10000)));
        setInputFocus();
    };

    const onSelectLiveUnit = useCallback((searchResult: SearchResult<LiveUnitSearchData>) => {
        const { id, date_activated, date_deactivated } = searchResult.source;

        storeRecentSearch(search);
        closeSearchBody();
        dispatch(setSelectedUnit({
            id,
            isArchiveCabinet: false,
            isDeactivated: !isUnitActive(date_activated, date_deactivated),
        }));
    }, [search]);

    const onSelectArchiveCabinet = useCallback((cabinetId: number) => {
        storeRecentSearch(search);
        closeSearchBody();
        dispatch(setSelectedUnit({
            id: cabinetId,
            isArchiveCabinet: true,
            isDeactivated: false,
        }));
    }, [search]);

    const navigateToStream = (result: SearchResult<LiveUnitSearchData>) => {
        getStreamCameraForLiveUnit(result.source.id)
            .unwrap()
            .then((cameraId: number) => history.push(`/camera/${cameraId}/stream`));
    };

    const navigateToStreamAndClose = (result: SearchResult<LiveUnitSearchData>) => {
        navigateToStream(result);
        closeSearchBody();
    };

    const searchResultRenderer = useCallback((index, result: SearchResult<ResultTypes>) => {
        return (
            <VmsSearchResults
                index={index}
                result={result}
                searchRightsData={searchRightsData}
                navigateToStream={navigateToStream}
                pageType={pageType}
                navigateToStreamAndClose={navigateToStreamAndClose}
                closeSearchBody={closeSearchBody}
                onSelectLiveUnit={onSelectLiveUnit}
                onSelectArchiveCabinet={onSelectArchiveCabinet}
                onClientSelected={onClientSelected}
                onRegionSelected={onRegionSelected}
                />
    );


    }, [searchRightsData, search, pageType]);

    const fsClasses = () => {
        let classes = FullStory.names.classes.searchField.common;

        switch (pageType) {
            case PageType.Map: classes += " " + FullStory.names.classes.searchField.map;
                break;
            case PageType.Stream: classes += " " + FullStory.names.classes.searchField.stream;
                break;
            case PageType.History: classes += " " + FullStory.names.classes.searchField.history;
                break;
        }

        return classes;
    };

    return (
        <Box className={`${providedClasses.root} ${classes.root} ${searchOpen ? classes.searchInputIncrease : classes.searchInputDecrease}`}>
            <ClickAwayListener onClickAway={closeSearchBody}>
                <div className={classes.searchBodyContainer}>
                    {/* <div onClick={()=> {history.push("/");}}>Back to map</div> */}
                    <Box onClick={openSearchBody} >
                        <SearchInput inputClasses={fsClasses()} search={search} onSearchChange={onSearchChange} inputRef={inputRef} setInputFocus={setInputFocus} setSearchOpen={setSearchOpen} handleMapUpdate={()=> dispatch(setMapSearchArg(formSearchQueryArg(search, 10000)))}/>
                    </Box>
                    <Box className={`${classes.searchBody} ${searchOpen ? "" : classes.searchBodyHide}`}>
                        {isFetching ? <LinearProgress /> : <div style={{ height: 4 }} />}
                        <Box>

                            {(searchRightsData && (searchRightsData.data.is_multi_client || searchRightsData.data.has_regions) && !(search.searchType != SearchType.Default || search.selectedClient || search.selectedRegion)) &&
                                <Box className={classes.searchSection}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom={true}><b>Filter By</b></Typography>
                                    {searchRightsData.data.is_multi_client &&
                                        <Button className={classes.filterByButton} variant="outlined" size="small" startIcon={<Domain color="action" />} onClick={searchForClients}><b>Accounts</b></Button>
                                    }
                                    {searchRightsData.data.has_regions &&
                                        <Button className={classes.filterByButton} variant="outlined" size="small" startIcon={<Domain color="action" />} onClick={searchForRegions}><b>Regions</b></Button>
                                    }
                                </Box>
                            }
                            {!(search.searchQuery || search.searchType != SearchType.Default || search.selectedClient || search.selectedRegion) &&
                                <Box className={classes.searchSection}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom={true}><b>Recent Searches</b></Typography>
                                    <RecentSearches />
                                </Box>
                            }
                            <Box className={classes.resultSection}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom={true}><b>Results ({isSuccess && <span>{total}</span>})</b></Typography>
                                <List>
                                    <Virtuoso
                                        style={{ height: "500px" }}
                                        data={results}
                                        itemContent={searchResultRenderer}
                                        endReached={loadMore}
                                        overscan={5}
                                    />
                                </List>
                            </Box>
                        </Box>
                    </Box>
                </div>
            </ClickAwayListener>
        </Box>
    );
};

export default SearchBar;
